import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { Icon } from '../Icon';
import { FileDetails, FilePreview, FileUploadContent, FileUploadInput } from './components';
import styles from './FileUpload.module.css';
import { useDragAndDrop, useFilePreview, useFileValidation } from './hooks';
import { formatAcceptText, formatMaxSizeLabel, getAriaLabel, getContainerClassName, getDataTestId, getDefaultAcceptTypes, getDisabledLabelClassName, getEnabledLabelClassName, getLabelStyle, getTitle, shouldShowClearButton, shouldShowFileInfo, shouldShowPreview, } from './utils';
export function FileUploadBase({ value, onChange, acceptTypes, error, setError, maxSizeKB = 2048, maxWidth = 2000, maxHeight = 2000, disabled = false, showClearButton = true, type = 'default', size = 'large', dataTestId, displayMode = 'default', inputId, ariaDescribedBy, }) {
    const finalAcceptTypes = acceptTypes ?? getDefaultAcceptTypes(type);
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const generatedInputId = useId();
    const [fileError, setFileError] = useState(null);
    const [fileInfo, setFileInfo] = useState(null);
    const { previewUrl, revokePreviewUrl, setPreviewFromFile } = useFilePreview({
        value: value ?? null,
        type,
    });
    const { validateFileWithDimensions } = useFileValidation({
        acceptTypes: finalAcceptTypes,
        maxSizeKB,
        maxWidth,
        maxHeight,
        type,
    });
    const processFile = async (file) => {
        if (disabled)
            return;
        const errorMsg = await validateFileWithDimensions(file);
        if (errorMsg) {
            setFileError(errorMsg);
            return;
        }
        setFileError(null);
        if (type === 'image') {
            setPreviewFromFile(file);
        }
        onChange?.(file);
    };
    const { isDragging, handleDragEnter, handleDragOver, handleDragLeave, handleDrop: handleDropFile, } = useDragAndDrop({
        disabled,
        onDrop: processFile,
    });
    useEffect(() => {
        if (!error && fileError && setError) {
            setError(fileError);
        }
    }, [fileError, error, setError]);
    useEffect(() => {
        if (value && typeof value === 'object' && displayMode === 'details') {
            setFileInfo({
                file: value,
                uploadedAt: new Date(),
            });
        }
        else if (!value) {
            setFileInfo(null);
        }
    }, [value, displayMode]);
    const handleFileChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file)
            return;
        await processFile(file);
    };
    const handleRemoveFile = () => {
        if (disabled)
            return;
        if (type === 'image') {
            revokePreviewUrl();
        }
        setFileError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onChange?.(null);
    };
    const hasValue = Boolean(value);
    const ariaLabel = getAriaLabel(type, hasValue);
    const title = getTitle(type, t);
    const containerClassName = [styles.container, styles[getContainerClassName(size)]].join(' ');
    const labelClassName = disabled
        ? `${styles.fileUpload} ${styles[getDisabledLabelClassName()]}`
        : `${styles.fileUpload} ${styles[getEnabledLabelClassName()]}`;
    const labelStyle = getLabelStyle(disabled);
    const acceptTypesString = finalAcceptTypes.map(ext => `.${ext}`).join(',');
    const typesText = `${t('file-upload.types.prefix')}${formatAcceptText(finalAcceptTypes)}${t('file-upload.types.max-size-suffix', { size: formatMaxSizeLabel(maxSizeKB) })}`;
    const labelTestId = getDataTestId(dataTestId, 'label');
    const fileErrorTestId = getDataTestId(dataTestId, 'file-error');
    const errorContainerTestId = getDataTestId(dataTestId, 'error-container');
    const errorIconTestId = getDataTestId(dataTestId, 'error-icon');
    const errorMessageTestId = getDataTestId(dataTestId, 'error-message');
    const resolvedInputId = inputId ?? `${generatedInputId}-input`;
    const fileErrorId = `${resolvedInputId}-validation-error`;
    const externalErrorId = `${resolvedInputId}-error`;
    const hasError = Boolean(fileError || error);
    const describedBy = [ariaDescribedBy, fileError ? fileErrorId : undefined, error ? externalErrorId : undefined]
        .filter((value) => Boolean(value))
        .join(' ') || undefined;
    const showPreview = shouldShowPreview(previewUrl, type);
    const showFileInfo = shouldShowFileInfo(value, type);
    const showClearButtonForImage = shouldShowClearButton(disabled, showClearButton);
    const showClearButtonForFile = shouldShowClearButton(disabled, showClearButton);
    const showDetails = displayMode === 'details' && fileInfo && value && typeof value === 'object';
    const detailsClassName = showDetails ? ` ${styles.fileUploadDetails}` : '';
    const labelClassNameWithDrag = isDragging
        ? `${labelClassName} ${styles.dragging}`
        : `${labelClassName}${detailsClassName}`;
    const renderFileContent = () => {
        if (showPreview && previewUrl) {
            return (_jsx(FilePreview, { previewUrl: previewUrl, showClearButton: showClearButtonForImage, dataTestId: dataTestId, onRemove: handleRemoveFile }));
        }
        if (showDetails && fileInfo) {
            return (_jsx(FileDetails, { fileInfo: fileInfo, showClearButton: showClearButtonForFile, dataTestId: dataTestId, onRemove: handleRemoveFile }));
        }
        return (_jsx(FileUploadContent, { title: title, typesText: typesText, value: value ?? null, isDragging: isDragging, showFileInfo: showFileInfo, showClearButton: showClearButtonForFile, dataTestId: dataTestId, onRemove: handleRemoveFile }));
    };
    return (_jsxs("div", { className: containerClassName, "data-testid": dataTestId, children: [_jsxs("label", { className: labelClassNameWithDrag, style: labelStyle, "aria-label": ariaLabel, "aria-invalid": hasError || undefined, "aria-describedby": describedBy, "data-testid": labelTestId, onDragEnter: handleDragEnter, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDropFile, children: [_jsx(FileUploadInput, { ref: fileInputRef, acceptTypesString: acceptTypesString, disabled: disabled, dataTestId: dataTestId, id: resolvedInputId, ariaInvalid: hasError, ariaDescribedBy: describedBy, onChange: handleFileChange }), renderFileContent(), fileError && (_jsx("div", { id: fileErrorId, className: styles.error, role: 'alert', "data-testid": fileErrorTestId, children: fileError }))] }), error && (_jsxs("div", { id: externalErrorId, className: styles.messageContainer, role: 'alert', "data-testid": errorContainerTestId, children: [_jsx(Icon, { name: 'inputError', alt: 'Error', className: styles.messageIcon, "data-testid": errorIconTestId }), _jsx("p", { className: styles.message, "data-testid": errorMessageTestId, children: error })] }))] }));
}
